import { Global } from '@emotion/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import TempoLogo from '@/assets/tempo-logo.svg';
import { LegendFooter } from '../published/LegendFooter';
import { PublishedTimeline } from '../published/PublishedTimeline';
import { exportTransparentGlobalStyles } from './ExportSwimlanePage.styles';
import { getBooleanQueryParam, getEffectivePreferences } from './export-utils';
import { useExportTimeline } from './useExportTimeline';

export const internalTimelineExportSearchSchema = z.object({
  token: z.string().min(1, 'Token cannot be empty'),
  title: z.coerce.string().optional(),
  branding: z.coerce.string().optional(),
  date: z.coerce.string().optional(),
  legend: z.coerce.string().optional(),
  vision: z.string().optional(),
  transparent: z.coerce.string().optional(),
});

export type InternalTimelineExportSearchParams = z.infer<typeof internalTimelineExportSearchSchema>;

export interface ExportTimelinePageProps {
  viewId: number;
  visualizationId: number;
  searchParams: InternalTimelineExportSearchParams;
}

export function ExportTimelinePage({
  viewId,
  visualizationId,
  searchParams,
}: ExportTimelinePageProps) {
  const { t, i18n } = useTranslation();
  const query = useExportTimeline({ viewId, visualizationId, token: searchParams.token });

  const transparentBackground = getBooleanQueryParam(searchParams.transparent, false);

  useEffect(() => {
    if (transparentBackground) {
      document.documentElement.classList.add('export-transparent');
    }
    return () => {
      document.documentElement.classList.remove('export-transparent');
    };
  }, [transparentBackground]);

  if (query.isPending) {
    return (
      <output className="flex items-center justify-center h-screen text-xl">
        {t(($) => $.views.published.loading)}
      </output>
    );
  }

  if (query.isError) {
    return (
      <div
        className="flex items-center justify-center h-screen text-xl text-text-error"
        role="alert"
      >
        {t(($) => $.views.published.errorLoading, { message: query.error.message })}
      </div>
    );
  }

  const { metadata, legendItems } = query.data;

  const preferences = getEffectivePreferences(metadata, searchParams);

  const visionStatement =
    searchParams.vision !== undefined ? searchParams.vision : metadata.visionStatement;

  const showHeader = preferences.showTitle || preferences.showVisionStatement;

  const publishDateValue = metadata.publishedAt || metadata.createdAt;
  const publishDate = publishDateValue
    ? new Date(publishDateValue).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <>
      {transparentBackground && <Global styles={exportTransparentGlobalStyles} />}
      <div
        className="w-screen flex flex-col"
        style={{ backgroundColor: 'var(--export-bg-color, white)' }}
        data-export-target="root"
      >
        {showHeader && (
          <header
            className="flex-none px-8 pt-6 pb-4"
            style={{ backgroundColor: 'var(--export-bg-color, white)' }}
            data-export-target="header"
          >
            {preferences.showTitle && (
              <h1 className="text-2xl font-bold text-text-neutral-primary mb-2">
                {metadata.title}
              </h1>
            )}
            {preferences.showVisionStatement && visionStatement && (
              <p className="text-sm text-text-neutral-secondary whitespace-pre-wrap">
                {visionStatement}
              </p>
            )}
          </header>
        )}

        <div className="w-full" data-publicationid="timeline-main-scrollable-container">
          <PublishedTimeline
            data={query.data}
            searchParams={{}}
            showLegend={false}
            isExportMode={true}
          />
        </div>

        {(preferences.showPublishDate ||
          preferences.showTempoBranding ||
          preferences.showLegend) && (
          <footer
            className="flex-none py-4 w-full"
            style={{ backgroundColor: 'var(--export-bg-color, white)' }}
            data-export-target="footer"
          >
            {preferences.showLegend && legendItems && legendItems.length > 0 && (
              <nav
                aria-label={t(($) => $.views.export.roadmapLegendAriaLabel)}
                className="w-full mb-3"
                data-export-target="footer-legend"
              >
                <LegendFooter items={legendItems} />
              </nav>
            )}

            <div className="flex items-center justify-between px-8">
              <div className="text-sm text-text-neutral-secondary">
                {preferences.showPublishDate && publishDate && (
                  <time dateTime={publishDateValue} data-export-target="footer-date">
                    {t(($) => $.views.published.publishedOn, { date: publishDate })}
                  </time>
                )}
              </div>
              <div className="flex items-center gap-3">
                {preferences.showTempoBranding && (
                  <div className="flex items-center gap-2" data-export-target="footer-branding">
                    <img src={TempoLogo} alt="Tempo" className="h-6 w-6" />
                    <span className="text-sm font-semibold text-text-neutral-primary">
                      {t(($) => $.views.export.tempoBrandingName)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </footer>
        )}
      </div>
    </>
  );
}
